import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--color-primary-500)] text-white hover:bg-[var(--color-primary-600)]",
        destructive:
          "bg-[var(--color-red-500)] text-white hover:bg-[var(--color-red-600)]",
        outline:
          "border border-[var(--color-border-default)] bg-white hover:bg-[var(--color-bg-subtle-gray)] hover:text-[var(--color-text-primary)]",
        secondary:
          "bg-[var(--color-grey-100)] text-[var(--color-text-primary)] hover:bg-[var(--color-grey-200)]",
        ghost:
          "hover:bg-[var(--color-bg-subtle-gray)] hover:text-[var(--color-text-primary)]",
        link: "text-[var(--color-primary-500)] underline-offset-4 hover:underline",
        orange:
          "bg-[var(--color-orange-500)] text-white hover:bg-[var(--color-orange-600)]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
